import React from 'react'
import { PageProps, graphql } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'

import { Container, Pagination, Seo } from 'components'
import { ArticleCard, CategoryList } from 'components/blog'
import { Layout } from 'components/layouts'
import { toAbsoluteUrl } from 'services/file'
import { classNames } from 'services/string'

export type BlogCategoryArticlesListPageContext = {
  limit: number
  skip: number
  numPages: number
  currentPage: number
  name: string
  slug: string
}

export const Head = ({ pageContext }: PageProps<Queries.CategoryArticlesListQuery, BlogCategoryArticlesListPageContext>) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Blog',
        item: toAbsoluteUrl('/blog'),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: pageContext.name,
        item: toAbsoluteUrl(`/blog/categoria/${pageContext.slug}`),
      },
    ],
  }

  return (
    <Seo
      seo={{
        structuredData,
        metaTitle: `Artigos sobre ${pageContext.name}`,
        metaDescription: `Veja os artigos da categoria ${pageContext.name} no Blog da Hubs.`,
      }}
    />
  )
}

export const BlogCategoryArticlesList = ({
  data,
  pageContext,
}: PageProps<Queries.CategoryArticlesListQuery, BlogCategoryArticlesListPageContext>) => {
  const { articles, highlightedArticles } = data
  const [highlightedArticle] = highlightedArticles.nodes

  const generatePaginatedLink = (page: number) => {
    if (page <= 1) return `/blog/categoria/${pageContext.slug}`
    return `/blog/categoria/${pageContext.slug}/pagina/${page}`
  }

  const articleNodes = articles.nodes || []

  return (
    <Layout>
      <Container className='pt-5'>
        <section className='w-full flex flex-row items-center justify-around'>
          <div className='md:w-1/2'>
            <h1 className='font-bold text-3xl'>Artigos sobre {pageContext.name}</h1>
            <h2 className='font-light text-xl'>
              No Blog da Hubs você encontra artigos e dicas para você se aprimorar e cuidar da saúde financeira da sua empresa.
            </h2>
          </div>
          <StaticImage
            src='../images/icons/newspaper.svg'
            alt='Ícone de Jornal'
            className='absolute mt-40 sm:mt-80 md:mt-0 md:relative max-w-1/2'
            height={440}
            placeholder='blurred'
          />
        </section>

        <div className={classNames('grid lg:grid-cols-3 mt-10 gap-y-10 lg:gap-x-5 xl:gap-x-10', articleNodes.length === 0 ? 'mb-20' : '')}>
          <div className='flex flex-col lg:col-span-2'>
            <h2 className='font-semibold text-3xl mb-10'>Artigo recente</h2>

            {highlightedArticle && <ArticleCard article={highlightedArticle} size='lg' />}
          </div>

          <aside className='flex flex-col gap-10 order-first lg:order-last'>
            <div className='lg:invisible w-full h-1 bg-white bg-opacity-70 rounded-xl'>&nbsp;</div>
            <CategoryList className='rounded-xl drop-shadow-xl bg-white bg-opacity-50 p-8 mt-2 lg:mt-9' />
          </aside>
        </div>

        {articleNodes.length >= 1 && (
          <>
            <div className='mt-14'>
              <h2 className='font-semibold text-3xl'>Artigos destacados</h2>

              <div className='mt-10 lg:place-items-start grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-8'>
                {articleNodes.map((article: Queries.ArticleCardFragment) => (
                  <ArticleCard article={article} key={article.id} />
                ))}
              </div>
            </div>

            <div className='mt-20'>
              <Pagination currentPage={pageContext.currentPage} numPages={pageContext.numPages} link={generatePaginatedLink} />
            </div>
          </>
        )}
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query CategoryArticlesList($slug: String!, $limit: Int!, $skip: Int!) {
    highlightedArticles: allStrapiArticle(limit: 1, sort: [{ createdAt: DESC }], filter: { category: { slug: { eq: $slug } } }) {
      nodes {
        ...ArticleCard
      }
    }

    articles: allStrapiArticle(limit: $limit, skip: $skip, sort: [{ createdAt: DESC }], filter: { category: { slug: { eq: $slug } } }) {
      nodes {
        ...ArticleCard
      }
    }
  }
`

export default BlogCategoryArticlesList
